import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import DownOutlined from '@ant-design/icons/DownOutlined'
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined'
import FolderOutlined from '@ant-design/icons/FolderOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import { Collapse, Empty, message, Tag, Tooltip, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { FileUploader } from 'features/files'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

import { formatCategory } from './helpers/categoryUtils'

const { Panel } = Collapse
const { Text } = Typography

export interface DocumentNavigationSidebarProps {
  unitId: string
  onUploadComplete?: (documentIds: ReadonlyArray<string>) => void
  showUploader?: boolean
  // Make it configurable instead of tightly coupled
  categories: ReadonlyArray<string>
  requiredCategories?: ReadonlyArray<string>
  metricsByCategory: ReadonlyMap<string, number>
  documentsByCategory: ReadonlyMap<
    string,
    ReadonlyArray<{
      entry_id?: string
      filename?: string
      visible_name?: string
    }>
  >
  isLoading: boolean
  onRefresh: () => Promise<void>
  selectedCategory?: string
  onCategorySelect: (category: string | undefined) => void
}

const PASS_ICON_COLOR = '#237804'
const FAIL_ICON_COLOR = '#a8071a'

export const DocumentNavigationSidebar: FC<DocumentNavigationSidebarProps> = ({
  unitId,
  onUploadComplete,
  showUploader = false,
  categories,
  requiredCategories = [],
  metricsByCategory,
  documentsByCategory,
  isLoading,
  onRefresh,
}) => {
  const { selectedDocumentId, setSelectedDocumentId } = useLibraDocument()
  const { rolodexPatch } = useRolodexEdit()

  // Use provided categories, sorted alphabetically
  const allCategoriesSorted = useMemo(() => {
    return [...categories].toSorted((a, b) => {
      const aLabel = formatCategory(a)
      const bLabel = formatCategory(b)
      return aLabel.localeCompare(bLabel)
    })
  }, [categories])

  const defaultExpandedKeys = useMemo(() => {
    // Expand required categories by default, or first 3 if no required ones
    const requiredCats = allCategoriesSorted.filter((cat) =>
      requiredCategories.includes(cat)
    )
    return requiredCats.length > 0
      ? requiredCats
      : allCategoriesSorted.slice(0, 3)
  }, [allCategoriesSorted, requiredCategories])

  const [expandedKeys, setExpandedKeys] =
    useState<string[]>(defaultExpandedKeys)
  const [draggedDocument, setDraggedDocument] = useState<{
    documentId: string
    currentCategory: string
  } | null>(null)
  const [dropTargetCategory, setDropTargetCategory] = useState<string | null>(
    null
  )

  const unitEntry = useMemo(() => {
    return {
      id: unitId,
      type: RolodexEntryType.UNIT,
    } as GeckoEntry
  }, [unitId])

  const handleDragStart = (
    documentId: string,
    currentCategory: string,
    e: React.DragEvent
  ) => {
    setDraggedDocument({ documentId, currentCategory })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedDocument(null)
    setDropTargetCategory(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (category: string) => {
    if (draggedDocument && draggedDocument.currentCategory !== category) {
      setDropTargetCategory(category)
    }
  }

  const handleDragLeave = () => {
    setDropTargetCategory(null)
  }

  const handleDrop = async (targetCategory: string, e: React.DragEvent) => {
    e.preventDefault()
    setDropTargetCategory(null)

    if (!draggedDocument) return
    if (draggedDocument.currentCategory === targetCategory) {
      setDraggedDocument(null)
      return
    }

    try {
      await rolodexPatch([
        {
          id: draggedDocument.documentId,
          type: RolodexEntryType.REPORT,
          data: {
            categories: [targetCategory],
          },
        },
      ])

      message.success(`Moved document to ${formatCategory(targetCategory)}`)
      await onRefresh()
    } catch (error) {
      console.error('Failed to update document category:', error)
      message.error('Failed to move document. Please try again.')
    } finally {
      setDraggedDocument(null)
    }
  }

  const renderDocumentList = (category: string) => {
    const count = metricsByCategory.get(category) ?? 0
    const label = formatCategory(category)

    if (count === 0) {
      return (
        <EmptyCategory
          onDragOver={handleDragOver}
          onDragEnter={() => handleDragEnter(category)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(category, e)}>
          <Empty
            description={`No ${label.toLowerCase()} documents`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '1rem 0' }}
          />
        </EmptyCategory>
      )
    }

    const documents = documentsByCategory.get(category) ?? []

    return (
      <DocumentList
        onDragOver={handleDragOver}
        onDragEnter={() => handleDragEnter(category)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(category, e)}>
        {documents.map((document, index) => {
          const isSelected = selectedDocumentId === document.entry_id
          const displayName =
            document.filename ||
            document.visible_name ||
            `Document ${index + 1}`
          const isDragging = draggedDocument?.documentId === document.entry_id
          return (
            <DocumentItem
              key={document.entry_id || `${category}-doc-${index}`}
              $isSelected={isSelected}
              $isDragging={isDragging}
              draggable
              onDragStart={(e) => {
                if (document.entry_id) {
                  handleDragStart(document.entry_id, category, e)
                }
              }}
              onDragEnd={handleDragEnd}
              onClick={() => {
                if (document.entry_id) {
                  setSelectedDocumentId(document.entry_id)
                }
              }}>
              <DocumentLabelWrapper>
                <Tooltip title={displayName} mouseEnterDelay={0.5}>
                  <DocumentLabel>{displayName}</DocumentLabel>
                </Tooltip>
              </DocumentLabelWrapper>
            </DocumentItem>
          )
        })}
      </DocumentList>
    )
  }

  const renderFolderHeader = (
    category: string,
    count: number,
    isExpanded: boolean
  ) => {
    const isRequired = requiredCategories.includes(category)
    const label = formatCategory(category)
    const hasDocuments = count > 0

    return (
      <FolderHeader>
        <FolderLeft>
          {isExpanded ? (
            <FolderOpenOutlined style={{ fontSize: 14, color: '#1890ff' }} />
          ) : (
            <FolderOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
          )}
          <FolderTitle>{label}</FolderTitle>
          {isRequired && (
            <>
              {hasDocuments ? (
                <CheckCircleFilled
                  style={{
                    color: PASS_ICON_COLOR,
                    fontSize: 12,
                    marginLeft: 4,
                  }}
                />
              ) : (
                <CloseCircleFilled
                  style={{
                    color: FAIL_ICON_COLOR,
                    fontSize: 12,
                    marginLeft: 4,
                  }}
                />
              )}
            </>
          )}
        </FolderLeft>
        <Tag color={isRequired ? (hasDocuments ? 'green' : 'red') : 'blue'}>
          {count}
        </Tag>
      </FolderHeader>
    )
  }

  if (isLoading) {
    return (
      <SidebarContainer>
        <LoadingState>Loading documents...</LoadingState>
      </SidebarContainer>
    )
  }

  return (
    <SidebarContainer>
      <SidebarHeader>
        <Text strong>Document Navigation</Text>
      </SidebarHeader>

      <FoldersContainer>
        <StyledCollapse
          activeKey={expandedKeys}
          onChange={(keys) => setExpandedKeys(keys as string[])}
          ghost
          expandIcon={({ isActive }) =>
            isActive ? <DownOutlined /> : <RightOutlined />
          }>
          {/* Category Folders - Each category is its own folder */}
          {allCategoriesSorted.map((category: string) => {
            const count = metricsByCategory.get(category) ?? 0
            const isExpanded = expandedKeys.includes(category)
            const isRequired = requiredCategories.includes(category)
            const isDropTarget = dropTargetCategory === category

            return (
              <Panel
                key={category}
                header={
                  <div
                    onDragOver={handleDragOver}
                    onDragEnter={() => handleDragEnter(category)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(category, e)}>
                    {renderFolderHeader(category, count, isExpanded)}
                  </div>
                }
                data-required={isRequired}
                data-drop-target={isDropTarget}>
                {renderDocumentList(category)}
              </Panel>
            )
          })}
        </StyledCollapse>
      </FoldersContainer>

      {/* Upload Section */}
      {showUploader && (
        <UploadSection>
          <UploadHeader>
            <UploadOutlined style={{ fontSize: 14, marginRight: 8 }} />
            <Text strong>Upload Documents</Text>
          </UploadHeader>
          <UploadContent>
            <FileUploader
              condensed
              showExistingFiles={false}
              entry={unitEntry}
              tags={{ unit: unitId }}
              onUploadComplete={(documentIds) => {
                // Refresh document data to update navigation
                onRefresh()
                // Call original handler if provided
                onUploadComplete?.(documentIds)
              }}
            />
          </UploadContent>
        </UploadSection>
      )}
    </SidebarContainer>
  )
}

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.forecast.background1};
  border-right: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
`

const SidebarHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  background: ${({ theme }) => theme.forecast.background2};
`

const FoldersContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    border: none;
    background: transparent;
  }

  .ant-collapse-header {
    padding: 0.75rem 1rem !important;
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
    align-items: center;
  }

  .ant-collapse-content {
    border: none;
    background: transparent;
  }

  .ant-collapse-content-box {
    padding: 0;
  }

  .ant-collapse-item[data-required='true'] .ant-collapse-header {
    border-left: 3px solid #f59e0b;
  }

  .ant-collapse-item[data-required='true'] .ant-collapse-header:hover {
    background: rgba(245, 158, 11, 0.05) !important;
  }

  .ant-collapse-item[data-drop-target='true'] .ant-collapse-header {
    background: rgba(24, 144, 255, 0.1) !important;
    border-left-color: ${({ theme }) => theme.forecast.primary};
  }
`

const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const FolderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const FolderTitle = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
`

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
`

const DocumentItem = styled.div<{
  $isSelected: boolean
  $isDragging?: boolean
}>`
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  user-select: none;
  border-left: 2px solid transparent;
  transition: all 0.2s ease;
  background: rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: center;
  min-width: 0;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  ${({ $isSelected }) =>
    $isSelected
      ? `
    background: rgba(24, 144, 255, 0.15) !important;
    border-left-color: rgba(24, 144, 255, 0.8) !important;
  `
      : ''}

  &:hover {
    background: rgba(24, 144, 255, 0.08) !important;
    border-left-color: rgba(24, 144, 255, 0.5) !important;
  }
`

const DocumentLabelWrapper = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
`

const DocumentLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  width: 100%;
`

const EmptyCategory = styled.div`
  padding: 0.5rem 1rem;
`

const LoadingState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const UploadSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  background: ${({ theme }) => theme.forecast.background2};
`

const UploadHeader = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
`

const UploadContent = styled.div`
  padding: 1rem;
`
